import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft } from 'lucide-react';
import ScriptToggle from '../components/ScriptToggle';
import AudioPlayer from '../components/AudioPlayer';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Situations = () => {
  const navigate = useNavigate();
  const { scriptMode } = usePreferences();
  const { token } = useAuth();
  const [selectedSituation, setSelectedSituation] = useState(null);
  const [situations, setSituations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listenedPhrases, setListenedPhrases] = useState(new Set()); // Track listened phrases

  useEffect(() => {
    fetchSituations();
  }, []);

  // Mark a phrase as listened and update progress
  const markPhraseListened = async (phraseIndex) => {
    if (!selectedSituation) return;
    
    const key = `${selectedSituation.id}:${phraseIndex}`;
    if (listenedPhrases.has(key)) return; // Already listened
    
    const newSet = new Set(listenedPhrases);
    newSet.add(key);
    setListenedPhrases(newSet);
    
    // Count listened phrases for this situation
    const listenedForSituation = Array.from(newSet).filter(k => k.startsWith(`${selectedSituation.id}:`)).length;
    const totalPhrases = selectedSituation.phrases?.length || 0;
    
    if (!token) return; // Guest mode - don't save to backend
    
    try {
      await axios.post(`${API}/user/content-progress`, {
        content_type: 'situation',
        content_id: selectedSituation.id,
        level: selectedSituation.level || 'beginner',
        phrases_completed: listenedForSituation,
        total_phrases: totalPhrases
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      console.log('Could not save situation progress');
    }
  };

  const handleSelectSituation = (situation) => {
    setSelectedSituation(situation);
    // Don't mark as viewed on open - only when phrases are listened
  };

  const fetchSituations = async () => {
    try {
      const response = await axios.get(`${API}/situations`);
      setSituations(response.data);
    } catch (error) {
      console.error('Failed to fetch situations:', error);
      toast.error('Erreur lors du chargement des situations');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <p className="font-nunito text-xl text-charcoal">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <nav className="bg-white border-b-2 border-stone-200 shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="tile-button bg-stone-200 text-charcoal p-2 rounded-lg hover:bg-stone-300"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <h1 className="font-playfair text-3xl font-bold text-secondary">
                🗺️ Situations
              </h1>
            </div>
            <ScriptToggle />
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        {!selectedSituation ? (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h2 className="font-playfair text-3xl font-bold text-charcoal mb-4">
                Choisissez une situation
              </h2>
              <p className="font-nunito text-lg text-charcoal/70">
                {situations.length} situations disponibles
              </p>
            </motion.div>

            {situations.length === 0 ? (
              <div className="text-center py-12">
                <p className="font-nunito text-xl text-charcoal/60">
                  Aucune situation pour le moment
                </p>
                <p className="font-nunito text-sm text-charcoal/50 mt-2">
                  L'administrateur peut en ajouter via l'interface admin
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {situations.map((situation, index) => (
                  <motion.button
                    key={situation.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleSelectSituation(situation)}
                    className="tile-button bg-white border-2 border-stone-200 rounded-2xl p-8 hover:border-secondary hover:shadow-lg card-hover text-left"
                  >
                    <div className="text-6xl mb-4">{situation.icon}</div>
                    <h3 className="font-playfair text-2xl font-bold text-charcoal mb-2">
                      {situation.title}
                    </h3>
                    <p className="font-nunito text-charcoal/70">
                      {situation.phrases?.length || 0} phrases essentielles
                    </p>
                  </motion.button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button
              onClick={() => setSelectedSituation(null)}
              className="tile-button bg-stone-200 text-charcoal font-nunito font-semibold px-4 py-2 rounded-lg mb-4 hover:bg-stone-300"
            >
              ← Retour
            </button>
            
            <h2 className="font-playfair text-4xl font-bold text-charcoal mb-8 flex items-center gap-4">
              <span className="text-6xl">{selectedSituation.icon}</span>
              {selectedSituation.title}
            </h2>

            <div className="space-y-4">
              {selectedSituation.phrases?.map((phrase, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 border-2 border-stone-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-secondary/10 rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold text-secondary">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      {scriptMode === 'arabic' ? (
                        <p className="font-amiri text-3xl text-primary mb-3 text-right">
                          {phrase.darija_arabic}
                        </p>
                      ) : (
                        <p className="font-nunito text-2xl font-bold text-primary mb-3">
                          {phrase.darija_phonetic}
                        </p>
                      )}
                      <p className="font-nunito text-lg text-charcoal">🇫🇷 {phrase.french}</p>
                      <p className="font-nunito text-lg text-charcoal">🇬🇧 {phrase.english}</p>
                    </div>
                    {/* Audio player with listening tracking */}
                    <AudioPlayer 
                      audioUrl={phrase.audio_url} 
                      size="md" 
                      onListened={() => markPhraseListened(index)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Situations;
